
/**
 * Core date utility functions for parsing and basic date handling
 */

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';

// Initialize plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);

/**
 * Parses a date string to a dayjs object
 * Handles common edge cases including the PostgreSQL/Supabase +00:00 timezone format
 * 
 * @param dateString The date string to parse
 * @returns A valid dayjs object or null if parsing fails
 */
export function parseDate(dateString: string | null | undefined): dayjs.Dayjs | null {
  if (!dateString) return null;
  
  try {
    // Handle specific date format issues
    let normalizedDateString = dateString;
    
    // Fix PostgreSQL/Supabase +00:00 timezone format to use Z for UTC
    if (normalizedDateString.includes('+00:00')) {
      normalizedDateString = normalizedDateString.replace('+00:00', 'Z');
    }
    
    // Handle other possible timezone formats that could cause issues
    if (normalizedDateString.match(/\+\d{2}:\d{2}$/)) {
      // Convert +HH:MM timezone format to Z for UTC if it's +00:00 in a different format
      const tzParts = normalizedDateString.match(/\+(\d{2}):(\d{2})$/);
      if (tzParts && tzParts[1] === '00' && tzParts[2] === '00') {
        normalizedDateString = normalizedDateString.replace(/\+\d{2}:\d{2}$/, 'Z');
      }
    }
    
    // Try multiple parsing strategies
    
    // 1. Use dayjs directly
    let dayjsDate = dayjs(normalizedDateString);
    
    // 2. If that fails, try Date.parse and create from timestamp
    if (!dayjsDate.isValid()) {
      const timestamp = Date.parse(normalizedDateString);
      if (!isNaN(timestamp)) {
        dayjsDate = dayjs(timestamp);
      }
    }
    
    // 3. If still invalid, try custom formats
    if (!dayjsDate.isValid()) {
      // Try common formats
      const formats = [
        'YYYY-MM-DDTHH:mm:ss.SSSZ',
        'YYYY-MM-DDTHH:mm:ssZ',
        'YYYY-MM-DD HH:mm:ss'
      ];
      
      for (const format of formats) {
        const parsed = dayjs(normalizedDateString, format);
        if (parsed.isValid()) {
          dayjsDate = parsed;
          break;
        }
      }
    }
    
    // Final validation
    if (!dayjsDate.isValid()) {
      console.warn(`Invalid date format: ${dateString} (normalized to: ${normalizedDateString})`);
      return null;
    }
    
    return dayjsDate;
  } catch (error) {
    console.error(`Error parsing date: ${dateString}`, error);
    return null;
  }
}

/**
 * Checks if a date string is valid
 * 
 * @param dateString The date string to validate
 * @returns true if the date is valid, false otherwise
 */
export function isValidDate(dateString: string | null | undefined): boolean {
  if (!dateString) return false;
  return parseDate(dateString) !== null;
}

/**
 * Gets the current date and time as an ISO string
 * 
 * @returns The current date and time in ISO format
 */
export function getCurrentISODateTime(): string {
  return dayjs().toISOString();
}

/**
 * Converts a date string to ISO format
 * 
 * @param dateString The date string to convert
 * @returns The date in ISO format, or null if invalid
 */
export function toISOString(dateString: string | null | undefined): string | null {
  const date = parseDate(dateString);
  return date ? date.toISOString() : null;
}
